// components/tracking/CurrentStatus.jsx - Improved RTO badge design
import React from 'react';
import { FaListUl, FaMapMarkerAlt, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import StatusBadge from './StatusBadge';

const CurrentStatus = ({ latestStatus, formatDate }) => {
  // Check if this is an RTO status
  const isRTOStatus = latestStatus?.order_status_code && [
    '130', '140', '150', '160', '170', '230', '250', '260'
  ].includes(latestStatus.order_status_code.toString()) || 
  latestStatus?.order_status_description?.toLowerCase().includes('rto');

  if (!latestStatus || !latestStatus.order_status_description) {
    return (
      <div className="bg-white rounded-r-2xl 2xl:rounded-l-none rounded-l-2xl border border-gray-200 2xl:border-l-0 p-6 h-full flex flex-col">
        <div className="text-center py-8 flex-1 flex flex-col justify-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <FaListUl className="text-gray-400 text-lg" />
          </div>
          <p className="text-gray-500 text-sm">No status information available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-r-2xl 2xl:rounded-l-none rounded-l-2xl border border-gray-200 2xl:border-l-0 p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
        <div className="p-2.5 bg-indigo-600 rounded-xl">
          <FaListUl className="text-white text-lg" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Current Status</h2>
          <p className="text-sm text-gray-500">Latest tracking update</p>
        </div>
      </div>
      
      {/* Status Content */}
      <div className="flex-1 space-y-4">
        {/* RTO Alert Banner - Only show for RTO statuses */}
        {isRTOStatus && (
          <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl p-4 shadow-lg border-l-4 border-red-600">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <FaExclamationTriangle className="text-lg" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-bold uppercase tracking-wider">Return to Origin</span>
                  <span className="text-lg">↩</span>
                </div>
                <p className="text-xs text-orange-100">
                  This shipment is being returned to the sender
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Status Badge */}
        <div className="text-center">
          <StatusBadge 
            status={latestStatus.order_status_description} 
            statusCode={latestStatus.order_status_code}
            size="default"
          />
        </div>
        
        {/* Location Information */}
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <FaMapMarkerAlt className="text-red-600 text-sm" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5">
                Current Location
              </p>
              <p className="font-semibold text-gray-900 text-sm">
                {latestStatus.order_location}
              </p>
            </div>
          </div>
        </div>
        
        {/* Last Updated */}
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FaClock className="text-blue-600 text-sm" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-blue-600 uppercase tracking-wide font-medium mb-0.5">
                Last Updated
              </p>
              <p className="font-semibold text-blue-900 text-sm">
                {formatDate(latestStatus.order_status_date_time_ist)}
              </p>
            </div>
          </div>
        </div>
        
        {/* Remarks if available */}
        {latestStatus.order_remarks && (
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
            <p className="text-xs text-amber-600 uppercase tracking-wide font-medium mb-2">
              Additional Information
            </p>
            <p className="text-amber-900 text-sm leading-relaxed italic">
              "{latestStatus.order_remarks}"
            </p>
          </div>
        )}

        {/* RTO Help Section - Only show for RTO statuses */}
        {isRTOStatus && (
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <p className="text-xs text-blue-600 uppercase tracking-wide font-medium mb-2">
              What does this mean?
            </p>
            <div className="text-sm text-blue-900 space-y-1">
              <p>• Your package is being returned to the original sender</p>
              <p>• This may be due to delivery issues or recipient unavailability</p>
              <p>• Contact customer support for more details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentStatus;